<template>
  <div class="uncertainty-dashboard">
    <div class="controls">
      <div class="control-item">
        <label for="model-name">Model Name:</label>
        <input
          id="model-name"
          v-model="modelName"
          type="text"
          placeholder="Enter model name"
        />
      </div>

      <div class="control-item">
        <label>Units:</label>
        <SearchableDropdownCheckboxes
          :options="unitOptions"
          v-model="selectedUnits"
          buttonLabel="Select Units…"
        />
      </div>

      <div class="control-item">
        <label for="aggregation">Aggregation:</label>
        <select id="aggregation" v-model="aggregationPeriod">
          <option
            v-for="opt in aggregationOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>

      <div class="control-item">
        <label for="aggregation">Radial unit:</label>
        <select id="aggregation" v-model="selectedRadialUnit">
          <option v-for="unit in radialUnitOptions" :key="unit" :value="unit">
            {{ unit }}
          </option>
        </select>
      </div>

      <div class="control-item">
        <button @click="startExperiment" :disabled="loading || !canStart">
          {{ loading ? "Starting…" : "Start Experiment" }}
        </button>
      </div>

      <div v-if="experimentId" class="control-item">
        <p>Experiment ID: {{ experimentId }}</p>
      </div>
    </div>

    <div v-if="metricsLoaded" class="results">
      <!-- Time-series charts -->
      <div class="chart-section">
        <h2>Average Uncertainty over Time</h2>
        <div class="chart-container">
          <VChart :option="avgChartOptions" autoresize />
        </div>
      </div>
      <div class="chart-section">
        <h2>Max Uncertainty over Time</h2>
        <div class="chart-container">
          <VChart :option="maxChartOptions" autoresize />
        </div>
      </div>

      <!-- Radial charts -->
      <div class="chart-section" v-if="selectedRadialUnit">
        <h2>
          Average Mean Uncertainty by Time of Day
          <span v-if="selectedRadialUnit === 'combined'">(Combined)</span>
          <span v-else>(Unit {{ selectedRadialUnit }})</span>
        </h2>
        <div class="chart-container">
          <VChart :option="radialAvgOptions" autoresize />
        </div>
      </div>
      <div class="chart-section" v-if="selectedRadialUnit">
        <h2>
          Average Max Uncertainty by Time of Day
          <span v-if="selectedRadialUnit === 'combined'">(Combined)</span>
          <span v-else>(Unit {{ selectedRadialUnit }})</span>
        </h2>
        <div class="chart-container">
          <VChart :option="radialMaxOptions" autoresize />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api_config } from "@/env.ts";
import axios from "axios";
import { getToken } from "@/keycloak-wrapper.ts";
import VChart from "vue-echarts";
import SearchableDropdownCheckboxes from "@/components/widgets/SearchableDropdownCheckboxes.vue";

export default {
  name: "UncertaintyDashboard",
  components: { VChart, SearchableDropdownCheckboxes },
  data() {
    return {
      token: null,
      modelName: "",
      unitOptions: [],
      selectedUnits: [],
      radialUnitOptions: [],
      selectedRadialUnit: "combined",
      loading: false,
      experimentId: null,
      metrics: [],
      metricsLoaded: false,
      avgChartOptions: {},
      maxChartOptions: {},
      radialAvgOptions: {},
      radialMaxOptions: {},
      aggregationOptions: [
        { value: "15-minute", label: "15 Minutes" },
        { value: "1-hour", label: "1 Hour" },
        { value: "1-day", label: "1 Day" },
        { value: "1-week", label: "1 Week" },
        { value: "1-month", label: "1 Month" },
      ],
      aggregationPeriod: "1-hour",
    };
  },
  computed: {
    canStart() {
      return this.modelName.trim() !== "" && this.selectedUnits.length > 0;
    },
  },
  watch: {
    aggregationPeriod: "updateCharts",
    selectedUnits(newUnits) {
      this.radialUnitOptions = ["combined"];
      this.selectedUnits.forEach((u) => this.radialUnitOptions.push(String(u)));
      if (
        this.selectedRadialUnit !== "combined" &&
        !newUnits.includes(this.selectedRadialUnit) &&
        newUnits.length
      ) {
        this.selectedRadialUnit = newUnits[0];
      }
      this.updateCharts();
    },
    selectedRadialUnit: "updateCharts",
    metrics: "updateCharts",
  },
  async mounted() {
    this.token = await getToken();
    this.fetchUnitOptions();
  },
  methods: {
    authHeaders() {
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      };
    },
    async fetchUnitOptions() {
      try {
        const { data } = await axios.get(
          `${api_config.ml_backend}/SentinexUnits`,
          { headers: this.authHeaders() }
        );
        this.unitOptions = data.unit_list.map((u) => ({ value: u, label: u }));
      } catch (e) {
        console.error("Error fetching units", e);
      }
    },
    async startExperiment() {
      if (!this.canStart) return;
      this.loading = true;
      this.metricsLoaded = false;
      try {
        const { data } = await axios.post(
          `${api_config.ml_backend}/uncertainty/start`,
          {
            model_name: this.modelName,
            unit_list: this.selectedUnits.join(","),
          },
          { headers: this.authHeaders() }
        );
        this.experimentId = data.experiment_id;
        await this.fetchMetrics();
      } catch (e) {
        console.error(e);
        alert(e.response?.data?.detail || e.message);
      } finally {
        this.loading = false;
      }
    },
    async fetchMetrics() {
      try {
        const params = new URLSearchParams();
        params.append("model_name", this.modelName);
        this.selectedUnits.forEach((u) => params.append("unit_list", u));
        const { data } = await axios.get(
          `${api_config.ml_backend}/uncertainty-metrics?${params.toString()}`,
          { headers: this.authHeaders() }
        );
        this.metrics = data.metrics.map((r) => ({
          SentinexID: Number(r.SentinexID),
          Datetime: new Date(r.Datetime),
          AvgUncertaintyScore: r.AvgUncertaintyScore,
          MaxUncertaintyScore: r.MaxUncertaintyScore,
        }));
        this.metricsLoaded = true;
      } catch (e) {
        console.error(e);
      }
    },
    getBucketStart(dt, period) {
      const date = new Date(dt);
      switch (period) {
        case "15-minute": {
          const m = Math.floor(date.getMinutes() / 15) * 15;
          return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            m
          );
        }
        case "1-hour":
          return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours()
          );
        case "1-day":
          return new Date(date.getFullYear(), date.getMonth(), date.getDate());
        case "1-week": {
          const day = date.getDay();
          const diff = date.getDate() - day + (day === 0 ? -6 : 1);
          return new Date(date.getFullYear(), date.getMonth(), diff);
        }
        case "1-month":
          return new Date(date.getFullYear(), date.getMonth(), 1);
      }
    },
    updateCharts() {
      const units = this.selectedUnits.map((u) => Number(u));
      const { metrics, aggregationPeriod, selectedRadialUnit } = this;
      if (!metrics.length || !units.length) return;

      // Time-series aggregation
      const agg = new Map();
      metrics.forEach((m) => {
        if (!units.includes(m.SentinexID)) return;
        const ts = this.getBucketStart(m.Datetime, aggregationPeriod).getTime();
        const key = `${m.SentinexID}-${ts}`;
        if (!agg.has(key)) agg.set(key, { sumA: 0, sumM: 0, count: 0 });
        const entry = agg.get(key);
        entry.sumA += m.AvgUncertaintyScore;
        entry.sumM += m.MaxUncertaintyScore;
        entry.count += 1;
      });
      const buckets = Array.from(
        new Set(Array.from(agg.keys()).map((k) => Number(k.split("-").pop())))
      )
        .sort((a, b) => a - b)
        .map((ts) => new Date(ts).toISOString());
      const legend = units.map(String);

      const avgSeries = units.map((u) => ({
        name: String(u),
        type: "line",
        showSymbol: false,
        data: buckets.map((iso) => {
          const t = new Date(iso).getTime();
          const key = `${u}-${t}`;
          if (!agg.has(key)) return [iso, null];
          const { sumA, count } = agg.get(key);
          return [iso, Number((sumA / count).toFixed(2))];
        }),
      }));

      const maxSeries = units.map((u) => ({
        name: String(u),
        type: "line",
        showSymbol: false,
        data: buckets.map((iso) => {
          const t = new Date(iso).getTime();
          const key = `${u}-${t}`;
          if (!agg.has(key)) return [iso, null];
          const { sumM, count } = agg.get(key);
          return [iso, Number((sumM / count).toFixed(2))];
        }),
      }));

      this.avgChartOptions = {
        tooltip: { trigger: "axis" },
        legend: { data: legend },
        xAxis: { type: "time", axisLabel: { rotate: 45 } },
        yAxis: { type: "value", name: "Avg Uncertainty" },
        dataZoom: [
          { type: "slider", xAxisIndex: 0, start: 0, end: 100 },
          { type: "inside", xAxisIndex: 0, start: 0, end: 100 },
        ],
        series: avgSeries,
      };

      this.maxChartOptions = {
        ...this.avgChartOptions,
        yAxis: { type: "value", name: "Max Uncertainty" },
        series: maxSeries,
      };

      // Radial aggregation
      const slots = 96;
      const slotAgg = Array.from({ length: slots }, () => ({
        sA: 0,
        sM: 0,
        c: 0,
      }));
      let seriesName = "";

      if (selectedRadialUnit === "combined") {
        seriesName = "Combined";
        metrics.forEach((m) => {
          if (!units.includes(m.SentinexID)) return;
          const idx =
            m.Datetime.getHours() * 4 +
            Math.floor(m.Datetime.getMinutes() / 15);
          const e = slotAgg[idx];
          e.sA += m.AvgUncertaintyScore;
          e.sM += m.MaxUncertaintyScore;
          e.c += 1;
        });
      } else {
        const id = Number(selectedRadialUnit);
        seriesName = `Unit ${id}`;
        metrics.forEach((m) => {
          if (m.SentinexID !== id) return;
          const idx =
            m.Datetime.getHours() * 4 +
            Math.floor(m.Datetime.getMinutes() / 15);
          const e = slotAgg[idx];
          e.sA += m.AvgUncertaintyScore;
          e.sM += m.MaxUncertaintyScore;
          e.c += 1;
        });
      }

      const labels = slotAgg.map((_, i) =>
        i % 4 === 0 ? String(Math.floor(i / 4)).padStart(2, "0") + ":00" : ""
      );
      const avgVals = slotAgg.map((e) =>
        e.c ? Number((e.sA / e.c).toFixed(2)) : 0
      );
      const maxVals = slotAgg.map((e) =>
        e.c ? Number((e.sM / e.c).toFixed(2)) : 0
      );
      const mA = Math.max(...avgVals);
      const mM = Math.max(...maxVals);
      const indA = labels.map((name) => ({ name, min: 0, max: mA }));
      const indM = labels.map((name) => ({ name, min: 0, max: mM }));

      this.radialAvgOptions = {
        tooltip: { trigger: "item" },
        visualMap: {
          show: true,
          min: 0,
          max: mA,
          orient: "vertical",
          left: "right",
          inRange: { color: ["#e0f7fa", "#006064"] },
        },
        radar: {
          indicator: indA,
          shape: "polygon",
          splitNumber: 4,
          axisLine: { lineStyle: { color: "#ccc" } },
        },
        series: [
          {
            type: "radar",
            data: [
              { value: avgVals, name: seriesName, areaStyle: { opacity: 0.4 } },
            ],
            symbol: "circle",
            symbolSize: 6,
          },
        ],
      };

      this.radialMaxOptions = {
        tooltip: { trigger: "item" },
        visualMap: {
          show: true,
          min: 0,
          max: mM,
          orient: "vertical",
          left: "right",
          inRange: { color: ["#ffe0b2", "#e65100"] },
        },
        radar: {
          indicator: indM,
          shape: "polygon",
          splitNumber: 4,
          axisLine: { lineStyle: { color: "#ccc" } },
        },
        series: [
          {
            type: "radar",
            data: [
              { value: maxVals, name: seriesName, areaStyle: { opacity: 0.4 } },
            ],
            symbol: "circle",
            symbolSize: 6,
          },
        ],
      };
    },
  },
};
</script>

<style scoped>
.uncertainty-dashboard {
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 1rem;
  height: 100%;
}
.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
}
.control-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.results {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}
.chart-section {
  padding: 0.5rem;
  background: #f9f9f9;
  border: 1px solid var(--colour-border);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
}
.chart-container {
  flex: 1;
  min-height: 200px;
}
</style>
